// ŚREDNIA ARYTMETYCZNA — LICZYMY x, gdy jedna liczba ma postać a x + b
// a zawsze dodatnie: 2..8; średnia całkowita; wyjaśnienie bez nawiasów przy ax+b
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

function gcd(a, b) { a = Math.abs(a); b = Math.abs(b); while (b) { const t = a % b; a = b; b = t; } return a || 1; }
function simplify(p, q) { if (q < 0) { p = -p; q = -q; } const g = gcd(p, q); return [p / g, q / g]; }
function dfrac(p, q) { const [pp, qq] = simplify(p, q); return qq === 1 ? `${pp}` : `\\dfrac{${pp}}{${qq}}`; }

// ułamek mieszany
function mixedLatex(p, q) {
  let [pp, qq] = simplify(p, q);
  const neg = pp < 0;
  pp = Math.abs(pp);
  const whole = Math.floor(pp / qq);
  const rem = pp % qq;
  if (whole === 0 && rem === 0) return "0";
  if (whole === 0) return (neg ? "-" : "") + `\\dfrac{${rem}}{${qq}}`;
  if (rem === 0) return (neg ? "-" : "") + `${whole}`;
  return (neg ? "-" : "") + `${whole}\\; ${dfrac(rem, qq)}`;
}

// parse: całkowite, a/b, a b/c (mieszany), dziesiętne
function parseFraction(raw) {
  if (raw == null) return null;
  const s0 = String(raw).trim();
  if (!s0) return null;

  // mixed
  let m = s0.match(/^\s*([+-]?\d+)\s+(\d+)\s*\/\s*(\d+)\s*$/);
  if (m) {
    const w = parseInt(m[1], 10);
    const num = parseInt(m[2], 10);
    const den = parseInt(m[3], 10);
    if (den === 0) return null;
    const sign = w < 0 ? -1 : 1;
    const absW = Math.abs(w);
    const p = sign * (absW * den + num);
    return [p, den];
  }

  // improper
  m = s0.match(/^\s*([+-]?\d+)\s*\/\s*([+-]?\d+)\s*$/);
  if (m) { const den = parseInt(m[2], 10); if (den === 0) return null; return [parseInt(m[1], 10), den]; }

  // integer
  if (/^[+-]?\d+$/.test(s0)) return [parseInt(s0, 10), 1];

  // decimal
  const s = s0.replace(",", ".");
  if (/^[+-]?(?:\d+|\d*\.\d+)$/.test(s)) {
    const sign = s.startsWith("-") ? -1 : 1;
    const body = s.replace(/^[+-]/, "");
    const [ip, fp=""] = body.split(".");
    const k = fp.length;
    const p = sign * (parseInt(ip || "0", 10) * (10 ** k) + parseInt(fp || "0", 10));
    const q = 10 ** k;
    return [p, q];
  }
  return null;
}

// latex dla ax+b (a>0)
function axbLatex(a, b) {
  const coef = `${a}x`;
  if (b === 0) return coef;
  const sgn = b > 0 ? "+" : "-";
  const bb = Math.abs(b);
  return `${coef}\\, ${sgn}\\, ${bb}`;
}

// ========== GENERATOR: a ∈ {2,…,8}, średnia całkowita ==========
export function generateTask() {
  const n = 5 + Math.floor(Math.random()*3); // 5..7 liczb
  const others = Array.from({length: n-1}, () => 5 + Math.floor(Math.random()*21)); // 5..25
  const sumOthers = others.reduce((a,b)=>a+b,0);

  const a = 2 + Math.floor(Math.random()*7); // 2..8
  const b = -10 + Math.floor(Math.random()*21); // -10..10

  const mean = 10 + Math.floor(Math.random()*21); // 10..30 (całkowita)

  // x = (n*mean - (sumOthers + b)) / a
  const SplusB = sumOthers + b;
  const xNum = n*mean - SplusB;
  const xDen = a;

  const pos = Math.floor(Math.random()*n); // losowa pozycja ax+b

  return {
    type: "MeanArithmeticWithUnknownTask",
    data: { n, others, sumOthers, a, b, mean, pos, xNum, xDen },
    uid: `mean_axb_${Date.now()}_${Math.floor(Math.random()*1e9)}`
  };
}

// ========== TREŚĆ ==========
export function renderTask({ data }) {
  const axb = axbLatex(data.a, data.b);
  const items = [...data.others.map(v => `${v}`)];
  items.splice(data.pos, 0, axb);
  const listLatex = items.join(",\\,");

  return (
    <div style={{ textAlign:"left", margin:"8px 0 12px", lineHeight:1.6 }}>
      <div>
        <span>Podane liczby: </span>
        <InlineMath math={listLatex} />
        <span>. Wiadomo, że <b>średnia arytmetyczna</b> wynosi </span>
        <InlineMath math={`${data.mean}`} />
        <span>.</span>
      </div>
      <div style={{ marginTop: 4 }}>
        <span>Oblicz </span><InlineMath math={'x'} /><span>.</span>
      </div>
    </div>
  );
}

// ========== INPUT ==========
export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <div className="flex items-center gap-3" style={{ marginTop: 4 }}>
      <span><InlineMath math={'x = '} /></span>
      <FractionInput value={userAnswer} onChange={onChange} disabled={disabled} label="x" placeholder="np. 6, 9/2, 3 1/2" />
    </div>
  );
}

// ========== SPRAWDZANIE ==========
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || !userAnswer.raw) return { correct:false, message:"Podaj wartość x." };
  const parsed = parseFraction(userAnswer.raw);
  if (!parsed) return { correct:false, message:"Nieprawidłowy zapis (np. 6, 9/2, 3 1/2)." };

  const uv = parsed[0]/parsed[1];
  const xCorrect = data.xNum / data.xDen;
  const correct = Math.abs(uv - xCorrect) <= 0.01 + 1e-12;

  const expectedMixed = mixedLatex(data.xNum, data.xDen);
  const expectedImproper = dfrac(data.xNum, data.xDen);
  return { correct, expected: `x = ${expectedMixed} \\;(=\\; ${expectedImproper})` };
}

// ========== ODPOWIEDŹ KRÓTKA ==========
export function renderAnswer({ data }) {
  const xMixed = mixedLatex(data.xNum, data.xDen);
  return <InlineMath math={`x = ${xMixed}`} />;
}

// ========== PODGLĄD ODPOWIEDZI UŻYTKOWNIKA ==========
export function renderUserAnswer(userAnswer) {
  if (!userAnswer || !userAnswer.raw) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={formatLatexPreview(userAnswer.raw, 'x')} />;
}

// ========== WYJAŚNIENIE — bez dublowania ułamka/liczby, szyk równań zachowany ==========
export function renderExplanation({ data }) {
  const { n, others, a, b, mean, sumOthers, pos } = data;

  // zapis sumy (bez nawiasów przy ax+b)
  const axb = axbLatex(a, b);
  const itemsPlus = [...others.map(v => `${v}`)];
  itemsPlus.splice(pos, 0, axb);
  const sumLine = itemsPlus.join(" + ");

  const S  = sumOthers;
  const nm = n * mean;
  const Sb = S + b;

  const axTerm  = `${a}x`;
  const axPlusC = Sb === 0 ? axTerm : (Sb > 0 ? `${axTerm} + ${Sb}` : `${axTerm} - ${Math.abs(Sb)}`);

  // licznik i mianownik do końcowego x
  const num = nm - Sb;
  const den = a;

  // sprawdzenie czy ułamek się skraca
  const [sn, sd] = simplify(num, den);
  const reducible = (sn !== num) || (sd !== den);

  // formy zapisu wyniku
  const rawFrac  = `\\dfrac{${num}}{${den}}`; // nieskrócony
  const simpFrac = dfrac(num, den);           // skrócony (może być liczbą całkowitą)
  const mixed    = mixedLatex(num, den);      // mieszany (dla całkowitej = ta sama liczba)

  // zbuduj listę bez duplikatów (np. "20 = 20" zniknie)
  const parts = [];
  if (reducible) parts.push(rawFrac);
  parts.push(simpFrac, mixed);
  const uniq = [];
  for (const p of parts) {
    if (!uniq.includes(p)) uniq.push(p);
  }
  const lastLine = `x = ${uniq.join(' = ')}`;

  return (
    <div style={{ textAlign:"left", lineHeight:1.75 }}>
      <div><strong>Krok 1 — średnia i sprowadzenie do sumy</strong></div>
      <BlockMath math={`\\text{średnia} = \\dfrac{\\text{suma}}{n} = ${mean}`} />
      <BlockMath math={`\\dfrac{${sumLine}}{${n}} = ${mean} \\quad \\big|\\; \\cdot ${n}`} />
      <BlockMath math={`${sumLine} = ${n}\\cdot ${mean}`} />
      <BlockMath math={`${axPlusC} = ${nm}`} />

      <div style={{ marginTop: 8 }}><strong>Krok 2 — rozwiązujemy równanie</strong></div>
      <BlockMath math={`${axPlusC} = ${nm} \\quad \\big|\\; ${Sb >= 0 ? '- '+Sb : '+ '+Math.abs(Sb)}`} />
      <BlockMath math={`${axTerm} = ${nm} ${Sb >= 0 ? `- ${Sb}` : `+ ${Math.abs(Sb)}`} \\quad \\big|\\; : ${a}`} />
      <BlockMath math={lastLine} />
    </div>
  );
}
